import React, { useEffect, useState } from 'react';
import {
  buildBudgetSearchHighlightSegments,
  normalizeBudgetSearchQuery,
  type BudgetNoteSearchHighlightSegment,
} from '../../utils/budgetNoteSearch';

function textIncludesQuery(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

export function renderSearchHighlightSegments(
  segments: BudgetNoteSearchHighlightSegment[]
): React.ReactNode {
  return segments.map((seg, i) => {
    if (seg.type === 'text') {
      return <React.Fragment key={`t-${i}`}>{seg.value}</React.Fragment>;
    }
    return (
      <mark
        key={`h-${seg.matchId}`}
        className={'cp-budget-note-search-hit' + (seg.active ? ' is-active' : '')}
        data-budget-search-match={seg.matchId}
      >
        {seg.value}
      </mark>
    );
  });
}

export const BudgetNoteSearchHighlightedText: React.FC<{
  text: string;
  query: string;
  activeMatchId: string | null;
  buildMatchId: (start: number) => string;
  className?: string;
}> = ({ text, query, activeMatchId, buildMatchId, className }) => {
  const q = normalizeBudgetSearchQuery(query);
  if (!q) return <span className={className}>{text}</span>;
  const segments = buildBudgetSearchHighlightSegments(text, q, activeMatchId, buildMatchId);
  return <span className={className}>{renderSearchHighlightSegments(segments)}</span>;
};

export const BudgetNoteSearchMirroredInput: React.FC<{
  value: string;
  query: string;
  activeMatchId: string | null;
  buildMatchId: (start: number) => string;
  className: string;
  ariaLabel: string;
  onChange: (v: string) => void;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLInputElement>;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  readOnly?: boolean;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}> = ({
  value,
  query,
  activeMatchId,
  buildMatchId,
  className,
  ariaLabel,
  onChange,
  onPaste,
  onDoubleClick,
  inputMode,
  readOnly,
  placeholder,
  inputRef,
  onBlur,
}) => {
  const q = normalizeBudgetSearchQuery(query);
  const showHighlight = Boolean(q) && textIncludesQuery(value, q);
  const segments = showHighlight
    ? buildBudgetSearchHighlightSegments(value, q, activeMatchId, buildMatchId)
    : null;
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!showHighlight) setEditing(false);
  }, [showHighlight, q]);

  const showInlinePreview = Boolean(showHighlight && segments && (readOnly || !editing));

  const beginEditing = () => {
    if (readOnly) return;
    setEditing(true);
    requestAnimationFrame(() => inputRef?.current?.focus());
  };

  return (
    <div
      className={
        'cp-budget-note-search-field-wrap' + (showInlinePreview ? ' has-inline-preview' : '')
      }
    >
      {showInlinePreview ? (
        <div
          className={className + ' cp-budget-note-search-inline-preview'}
          role="textbox"
          aria-label={ariaLabel}
          aria-readonly={readOnly || undefined}
          tabIndex={readOnly ? -1 : 0}
          onMouseDown={(e) => {
            if (readOnly) return;
            e.preventDefault();
            beginEditing();
          }}
          onFocus={beginEditing}
        >
          {renderSearchHighlightSegments(segments!)}
        </div>
      ) : null}
      <input
        ref={inputRef}
        className={
          className + (showInlinePreview ? ' cp-budget-note-search-inline-measurer' : '')
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPaste={onPaste}
        onDoubleClick={onDoubleClick}
        onBlur={(e) => {
          setEditing(false);
          onBlur?.(e);
        }}
        onFocus={() => setEditing(true)}
        aria-label={showInlinePreview ? undefined : ariaLabel}
        aria-hidden={showInlinePreview || undefined}
        tabIndex={showInlinePreview ? -1 : undefined}
        inputMode={inputMode}
        readOnly={readOnly}
        placeholder={placeholder}
      />
    </div>
  );
};
